(() => {
const parseGalleryAlbums = text => {
 let data;
 try { data=JSON.parse(text || '[]'); } catch { return []; }
 if(!Array.isArray(data)) return [];
 return data.filter(album=>album && Array.isArray(album.images)).map((album,index)=>{
  const seen=new Set();
  const images=album.images.filter(photo=>photo && typeof photo.src==='string' && photo.src.trim()).map(photo=>({...photo,src:photo.src.trim()})).filter(photo=>{
   if(seen.has(photo.src)) return false;
   seen.add(photo.src);
   return true;
  });
  return {...album,label:album.label || `GALLERY ${index+1}`,title:album.title || '',images};
 }).filter(album=>album.images.length);
};
const buildGalleryPages = (albums, pageSize=4) => {
 const pages=[];
 for(let offset=0;offset<albums.length;offset+=pageSize) {
  const group=albums.slice(offset,offset+pageSize);
  const counts=group.map(()=>1);
  const total=Math.min(pageSize,group.reduce((sum,album)=>sum+album.images.length,0));
  while(counts.reduce((sum,count)=>sum+count,0)<total) {
   const candidates=group.map((album,index)=>index).filter(index=>counts[index]<group[index].images.length);
   candidates.sort((a,b)=>counts[a]-counts[b] || group[b].images.length-group[a].images.length || a-b);
   counts[candidates[0]]++;
  }
  const slots=[];
  group.forEach((album,albumIndex)=>{
   for(let preview=0;preview<counts[albumIndex];preview++) {
    const photos=album.images.map((photo,index)=>({photo,index})).filter(({index})=>index%counts[albumIndex]===preview);
    slots.push({album,photos,preview});
   }
  });
  pages.push(slots);
 }
 return pages;
};
const createPhotoSlider = (slides,{count,initialIndex=0,onChange=()=>{},onActivate}) => {
 const clampPhoto=index=>Math.max(0,Math.min(count-1,index));
 let selected=clampPhoto(initialIndex);
 let animationFrame=0;
 let drag;
 let press;
 let suppressClick=false;
 const listeners=[];
 const listen=(type,handler,options)=>{
  slides.addEventListener(type,handler,options);
  listeners.push(()=>slides.removeEventListener(type,handler,options));
 };
 const photoStep=()=>slides.getBoundingClientRect().width+(parseFloat(getComputedStyle(slides).columnGap)||0);
 const update=()=>{
  selected=clampPhoto(Math.round(slides.scrollLeft/Math.max(1,photoStep())));
  onChange(selected);
 };
 const stopAnimation=()=>{
  cancelAnimationFrame(animationFrame);
  animationFrame=0;
  slides.classList.remove('is-settling');
 };
 const goTo=(index,animated=true)=>{
  stopAnimation();
  const target=clampPhoto(index)*photoStep();
  const from=slides.scrollLeft;
  const distance=target-from;
  slides.classList.add('is-settling');
  const finish=()=>{
   slides.scrollLeft=target;
   animationFrame=0;
   slides.classList.remove('is-settling');
   update();
  };
  if(!animated || Math.abs(distance)<1 || matchMedia('(prefers-reduced-motion: reduce)').matches) {
   finish();
   return;
  }
  const started=performance.now();
  const animate=now=>{
   const progress=Math.min(1,(now-started)/460);
   slides.scrollLeft=from+distance*(1-Math.pow(1-progress,3));
   if(progress<1) animationFrame=requestAnimationFrame(animate);
   else finish();
  };
  animationFrame=requestAnimationFrame(animate);
 };
 const move=direction=>goTo(selected+direction);
 slides.classList.toggle('is-static',count===1);
 listen('scroll',update,{passive:true});
 listen('wheel',stopAnimation,{passive:true});
 listen('keydown',event=>{
  if(event.key==='ArrowRight'||event.key==='ArrowLeft') {
   event.preventDefault();
   event.stopPropagation();
   move(event.key==='ArrowRight'?1:-1);
  }
 });
 listen('pointerdown',event=>{
  stopAnimation();
  if(onActivate && event.button===0) {
   press={x:event.clientX,y:event.clientY,left:slides.scrollLeft};
   suppressClick=false;
  }
  if(count<2 || event.pointerType!=='mouse'||event.button!==0) return;
  slides.classList.add('is-dragging');
  drag={x:event.clientX,left:slides.scrollLeft,index:selected,lastX:event.clientX,time:event.timeStamp,velocity:0,distance:0};
  slides.setPointerCapture(event.pointerId);
 });
 listen('pointermove',event=>{
  if(press && (Math.abs(event.clientX-press.x)>8 || Math.abs(event.clientY-press.y)>8)) suppressClick=true;
  if(!drag) return;
  drag.velocity=(drag.lastX-event.clientX)/Math.max(1,event.timeStamp-drag.time);
  drag.lastX=event.clientX;
  drag.time=event.timeStamp;
  drag.distance=drag.x-event.clientX;
  slides.scrollLeft=drag.left+drag.distance;
 });
 const endDrag=()=>{
  if(press && Math.abs(slides.scrollLeft-press.left)>8) suppressClick=true;
  press=null;
  if(!drag) return;
  const {index,distance,velocity}=drag;
  drag=null;
  const advance=Math.abs(distance)>photoStep()*.18 || (Math.abs(velocity)>.35 && Math.abs(distance)>8);
  goTo(index+(advance?Math.sign(distance):0));
  slides.classList.remove('is-dragging');
 };
 listen('pointerup',endDrag);
 listen('pointercancel',()=>{suppressClick=true;endDrag();});
 listen('lostpointercapture',endDrag);
 listen('dragstart',event=>event.preventDefault());
 if(onActivate) {
  listen('click',event=>{
   if(suppressClick) {
    suppressClick=false;
    event.preventDefault();
    event.stopPropagation();
    return;
   }
   onActivate();
  });
  listen('keydown',event=>{
   if((event.key==='Enter' || event.key===' ') && !event.repeat) {
    event.preventDefault();
    onActivate();
   }
  });
 }
 let previousStep=0;
 const observer=new ResizeObserver(()=>{
  const step=photoStep();
  if(Math.abs(step-previousStep)<.1) return;
  previousStep=step;
  goTo(selected,false);
 });
 observer.observe(slides);
 onChange(selected);
 const initialFrame=requestAnimationFrame(()=>goTo(initialIndex,false));
 return {move,goTo,dispose:()=>{
  observer.disconnect();
  cancelAnimationFrame(initialFrame);
  stopAnimation();
  drag=null;
  press=null;
  slides.classList.remove('is-dragging','is-static');
  listeners.forEach(remove=>remove());
 }};
};

const getOverviewEmbedUrl = value => {
 if(typeof value!=='string' || !value.trim()) return '';
 try {
  const url=new URL(value.trim());
  if(!['https:','http:'].includes(url.protocol)) return '';
  const host=url.hostname.toLowerCase();
  const parts=url.pathname.split('/').filter(Boolean);
  let id='';
  if(host==='youtu.be') id=parts[0] || '';
  else if(['youtube.com','www.youtube.com','m.youtube.com'].includes(host)) {
   if(parts[0]==='watch') id=url.searchParams.get('v') || '';
   else if(['shorts','embed'].includes(parts[0])) id=parts[1] || '';
  }
  if(!/^[A-Za-z0-9_-]{11}$/.test(id)) return '';
  const playlist=(url.searchParams.get('playlist') || '').split(',').filter(videoId=>/^[A-Za-z0-9_-]{11}$/.test(videoId));
  const queue=[...new Set(playlist)].filter(videoId=>videoId!==id);
  return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0${queue.length ? '&playlist='+queue.join(',') : ''}`;
 } catch { return ''; }
};

const initialize = () => {
 const stories=document.querySelector('[data-mkst-product-gallery]');
 if(stories && !stories.dataset.galleryReady) {
 stories.dataset.galleryReady='true';
 const labels=JSON.parse(stories.querySelector('[data-gallery-labels]').textContent);
 const label=(key,values={})=>Object.entries(values).reduce((text,[name,value])=>text.replaceAll('{'+name+'}',String(value)),labels[key]);
 const albums = parseGalleryAlbums(stories.querySelector('[data-gallery-albums]')?.textContent);
 const track = stories.querySelector('.wheel-stories__track');
 const dialog = stories.querySelector('dialog');
 const modalTrack = dialog.querySelector('.gallery-modal-track');
 const modalControls = dialog.querySelector('.gallery-modal-controls');
 const dotnav = dialog.querySelector('.gallery-modal-dotnav');
 const counter = dialog.querySelector('.gallery-modal-counter');
 let modalSlider;
 const createImage = photo => { const image = document.createElement('img'); image.src = photo.src; image.alt = photo.alt || ''; image.loading = 'lazy'; return image; };
 const openAlbum = (album, index) => {
  modalSlider?.dispose();
  dialog.querySelector('h3').textContent = album.label + ' — ' + album.title;
  modalTrack.replaceChildren(...album.images.map(photo => {const figure = document.createElement('figure'); const image = createImage(photo); image.loading = 'eager'; figure.append(image); return figure;}));
  const dots=album.images.map((photo,photoIndex)=>{
   const button=document.createElement('button');
   button.type='button';
   button.className='gallery-modal-dot';
   button.setAttribute('aria-label',label('photo',{current:photoIndex+1,total:album.images.length}));
   button.setAttribute('aria-controls',modalTrack.id);
   button.append(document.createElement('span'));
   button.addEventListener('click',()=>modalSlider?.goTo(photoIndex));
   return button;
  });
  dotnav.replaceChildren(...dots);
  modalControls.hidden=album.images.length<2;
  dialog.showModal();
  document.documentElement.style.overflow = 'hidden';
  let currentPhoto=-1;
  modalSlider=createPhotoSlider(modalTrack,{
   count:album.images.length,
   initialIndex:index,
   onChange:selected=>{
    if(selected===currentPhoto) return;
    currentPhoto=selected;
    counter.textContent=`${selected+1} / ${album.images.length}`;
    dots.forEach((dot,dotIndex)=>{
     if(dotIndex===selected) dot.setAttribute('aria-current','true');
     else dot.removeAttribute('aria-current');
    });
    const active=dots[selected];
    if(active.offsetLeft<dotnav.scrollLeft) dotnav.scrollLeft=active.offsetLeft;
    else if(active.offsetLeft+active.offsetWidth>dotnav.scrollLeft+dotnav.clientWidth) dotnav.scrollLeft=active.offsetLeft+active.offsetWidth-dotnav.clientWidth;
   }
  });
 };
 // One album cover selector, with three details from the selected album.
 const controls=stories.querySelector('.wheel-stories__controls');
 controls.hidden=true;
 stories.classList.add('product-albums');
 let selectedAlbum=-1, coverSlider;
 const cover=document.createElement('div');
 cover.className='product-album-cover';
 const covers=document.createElement('div');
 covers.className='wheel-story__slides product-album-track';
 covers.tabIndex=0;
 covers.setAttribute('role','button');
 covers.setAttribute('aria-haspopup','dialog');
 albums.forEach(album=>{const figure=document.createElement('figure');figure.append(createImage(album.images[0]));covers.append(figure);});
 const pagination=document.createElement('div');
 pagination.className='product-album-pagination';
 pagination.setAttribute('role','group');
 pagination.setAttribute('aria-label','Choose album');
 const dots=albums.map((album,i)=>{
  const button=document.createElement('button');button.type='button';
  button.setAttribute('aria-label',`Choose album ${i+1}: ${album.label}`);
  button.append(document.createElement('span'));
  button.addEventListener('click',()=>coverSlider.goTo(i));
  pagination.append(button);return button;
 });
 pagination.hidden=albums.length<2;
 cover.append(covers,pagination);
 track.replaceChildren(cover);
 const showAlbum=index=>{
  if(index===selectedAlbum)return;
  selectedAlbum=index;
  const album=albums[index];if(!album)return;
  const details=album.images.slice(1,4);
  track.style.setProperty('--album-columns',details.length+1);
  track.style.setProperty('--album-mobile-columns',Math.min(details.length+1,2));
  track.setAttribute('aria-label',album.label);
  covers.setAttribute('aria-label',`Open ${album.label}`);
  dots.forEach((dot,i)=>dot.setAttribute('aria-current',String(i===index)));
  track.querySelectorAll('.product-album-detail').forEach(node=>node.remove());
  details.forEach((photo,i)=>{
   const button=document.createElement('button');button.type='button';button.className='product-album-detail';
   button.setAttribute('aria-label',`Open ${album.label}, photo ${i+2}`);
   button.setAttribute('aria-haspopup','dialog');button.append(createImage(photo));
   button.addEventListener('click',()=>openAlbum(album,i+1));track.append(button);
  });
 };
 stories.hidden=!albums.length;
 if(albums.length) coverSlider=createPhotoSlider(covers,{count:albums.length,onChange:showAlbum,onActivate:()=>openAlbum(albums[selectedAlbum],0)});
 dialog.querySelector('.wheel-story-dialog__close').addEventListener('click',()=>dialog.close());
 dialog.addEventListener('close',()=>{
  modalSlider?.dispose();
  modalSlider=undefined;
  document.documentElement.style.overflow='';
 });
 dialog.addEventListener('click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}});
 stories.disposeGallery=()=>{coverSlider?.dispose();modalSlider?.dispose();if(dialog.open)dialog.close();};

 }
const overviewLink = document.querySelector('.explanation-video');
const overviewDialog = document.querySelector('.overview-dialog');
const overviewEmbedUrl = getOverviewEmbedUrl(overviewLink?.getAttribute('href'));
if(overviewLink) overviewLink.hidden=!overviewEmbedUrl || !overviewDialog;
if (overviewLink && overviewDialog && overviewEmbedUrl && !overviewLink.dataset.overviewReady) {
 overviewLink.dataset.overviewReady="true";
 overviewLink.setAttribute('aria-haspopup','dialog');
 overviewLink.addEventListener('click',event => {
  event.preventDefault();
  const frame = document.createElement('iframe');
  frame.src = overviewEmbedUrl;
  frame.title = overviewDialog.getAttribute('aria-label');
  frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
  frame.allowFullscreen = true;
  overviewDialog.querySelector('.overview-dialog__player').replaceChildren(frame);
  overviewDialog.showModal();
  document.documentElement.style.overflow = 'hidden';
 });
 overviewDialog.querySelector('button').addEventListener('click',()=>overviewDialog.close());
 overviewDialog.addEventListener('close',()=>{
  overviewDialog.querySelector('.overview-dialog__player').replaceChildren();
  document.documentElement.style.overflow = '';
 });
 overviewDialog.addEventListener('click',event=>{
  if(event.target===overviewDialog){const r=overviewDialog.getBoundingClientRect(); if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)overviewDialog.close();}
 });
}

};
initialize();
document.addEventListener('shopify:section:load',initialize);
document.addEventListener('shopify:section:unload',event=>event.target.querySelector('[data-mkst-product-gallery]')?.disposeGallery?.());
})();
